import React, { useState, useEffect } from 'react';
import { DEFAULT_PRICE_TABLE } from '../../config/constants';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, btn, inp } from '../../config/styles';
import { calcSectionScore, calcOverallScore } from '../../utils/products';
import { StatusBadge, Ring } from '../ui/shared';
import { OpsForm, TradingForm, CreativeForm, TechnicalForm, DigitalForm, StudioForm, StudioImagesReadOnly, GenericDeptForm, CollectionNameContext } from './forms';

// ─────────────────────────────────────────────────────────────
// STATUS PANEL  (approval workflow)
// ─────────────────────────────────────────────────────────────

export function StatusPanel({ p, roles, onUpdate }) {
  const { deptIds, deptLabels } = useTenantConfig();
  const isManager = roles?.includes('manager') ?? false;
  const depts = deptIds.filter(d => roles?.includes(d));
  const [notes, setNotes] = useState({ ...p.sectionNotes });

  // Keep notes in sync if product changes externally
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNotes({ ...p.sectionNotes }); }, [p.id]);

  const submitSection = (d) => {
    const status = p.sectionStatus[d];
    if (status === 'approved') return;
    onUpdate({
      ...p,
      sectionStatus: { ...p.sectionStatus, [d]: 'review' },
      sectionNotes:  { ...p.sectionNotes,  [d]: notes[d] || '' },
    });
  };

  const approveSection = (d) => onUpdate({ ...p, sectionStatus: { ...p.sectionStatus, [d]: 'approved' } });
  const returnSection  = (d) => onUpdate({ ...p, sectionStatus: { ...p.sectionStatus, [d]: 'progress' } });

  const sectionsToShow = isManager ? deptIds : depts;

  return (
    <div style={{ background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
      {isManager && (
        <div style={{ fontFamily: F.display, fontSize: 14, color: C.text, marginBottom: 12 }}>Section Approvals</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sectionsToShow.map(d => {
          const status = p.sectionStatus[d] || 'empty';
          const note   = p.sectionNotes?.[d] || '';
          return (
            <div key={d} style={{ padding: '10px 12px', background: C.white, borderRadius: 8, border: `1px solid ${C.e8}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 500, color: C.text }}>{deptLabels[d]}</span>
                  <StatusBadge status={status} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {isManager && status === 'review' && (
                    <>
                      <button style={btn('success', { fontSize: 11, padding: '4px 10px' })} onClick={() => approveSection(d)}>Approve</button>
                      <button style={btn('danger',  { fontSize: 11, padding: '4px 10px' })} onClick={() => returnSection(d)}>Return</button>
                    </>
                  )}
                  {!isManager && depts.includes(d) && (status === 'empty' || status === 'progress') && (
                    <button style={btn('primary', { fontSize: 11, padding: '4px 10px' })} onClick={() => submitSection(d)}>
                      Mark as ready for review
                    </button>
                  )}
                </div>
              </div>
              {/* Note display for manager */}
              {isManager && note && (
                <div style={{ marginTop: 8, fontSize: 12, color: C.text2, background: C.fa, padding: '6px 8px', borderRadius: 4 }}>
                  {note}
                </div>
              )}
              {/* Note input for dept user */}
              {!isManager && depts.includes(d) && (status === 'empty' || status === 'progress') && (
                <div style={{ marginTop: 8 }}>
                  <textarea
                    style={{ ...inp({ fontSize: 12, padding: '6px 8px', resize: 'vertical', minHeight: 56 }) }}
                    value={notes[d] || ''}
                    onChange={e => setNotes(prev => ({ ...prev, [d]: e.target.value }))}
                    placeholder="Add a note for the manager (optional)…"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PRODUCT EDITOR
// ─────────────────────────────────────────────────────────────

const SECTION_FORMS = {
  ops: OpsForm, trading: TradingForm, creative: CreativeForm,
  technical: TechnicalForm, digital: DigitalForm, studio: StudioForm,
};

export function ProductEditor({ product, roles, onUpdate, onBack, priceTable = DEFAULT_PRICE_TABLE, onPrev, onNext, productIndex, productCount, collectionName = '' }) {
  const tenantConfig = useTenantConfig();
  const { deptIds, deptLabels } = tenantConfig;
  const isManager = roles?.includes('manager') ?? false;
  const depts = deptIds.filter(d => roles?.includes(d));
  const defaultSection = depts.length > 0 ? depts[0] : 'ops';
  const [active, setActive] = useState(defaultSection);
  const canEdit = d => isManager || depts.includes(d);
  const Form = SECTION_FORMS[active];
  const score = calcOverallScore(product, tenantConfig);

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 52px)', background: C.white }}>
      {/* Sidebar */}
      <div style={{
        width: 224, borderRight: `1px solid ${C.e8}`, flexShrink: 0,
        background: C.fa, position: 'sticky', top: 52, height: 'calc(100vh - 52px)', overflowY: 'auto',
      }}>
        <div style={{ padding: '14px 16px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={onBack} style={{ ...btn('ghost', { padding: '4px 0', fontSize: 12, color: C.muted }), display: 'flex', alignItems: 'center', gap: 4 }}>
            ← Back to products
          </button>
          {productCount > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={onPrev} disabled={!onPrev} style={{ ...btn('secondary', { padding: '3px 10px', fontSize: 12 }), opacity: onPrev ? 1 : 0.35 }}>‹</button>
              <span style={{ fontSize: 11, color: C.muted, flex: 1, textAlign: 'center' }}>
                {productIndex + 1} / {productCount}
              </span>
              <button onClick={onNext} disabled={!onNext} style={{ ...btn('secondary', { padding: '3px 10px', fontSize: 12 }), opacity: onNext ? 1 : 0.35 }}>›</button>
            </div>
          )}
        </div>
        <div style={{ padding: '0 16px 16px', borderBottom: `1px solid ${C.e8}` }}>
          <div style={{ fontFamily: F.display, fontSize: 15, color: C.text, lineHeight: 1.3, marginBottom: 4 }}>
            {product.title || <span style={{ color: C.ph }}>Untitled product</span>}
          </div>
          <div style={{ fontFamily: F.mono, fontSize: 11, color: C.muted }}>{product.skuBase || '-'}</div>
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
            <Ring pct={score} size={54} stroke={5} />
          </div>
        </div>
        <nav style={{ padding: '8px 0' }}>
          {deptIds.map(d => {
            const status  = product.sectionStatus[d] || 'empty';
            const sScore  = calcSectionScore(product, d, tenantConfig);
            const isActive = active === d;
            const locked   = !canEdit(d);
            return (
              <button key={d} onClick={() => setActive(d)} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px', width: '100%', border: 'none', cursor: 'pointer',
                background: isActive ? C.white : 'transparent',
                borderLeft: `2px solid ${isActive ? C.text : 'transparent'}`,
                textAlign: 'left',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? C.text : C.text2, fontFamily: F.body }}>
                    {deptLabels[d]}
                  </div>
                  <div style={{ height: 2, background: C.e8, borderRadius: 2, marginTop: 5 }}>
                    <div style={{ height: '100%', background: sScore === 100 ? '#6ee7b7' : C.text, borderRadius: 2, width: `${sScore}%`, transition: 'width 0.3s' }} />
                  </div>
                </div>
                <div style={{ flexShrink: 0, fontSize: 12 }}>
                  {status === 'approved' && <span style={{ color: '#065f46' }}>✓</span>}
                  {status === 'review'   && <span style={{ color: '#92400e' }}>●</span>}
                  {locked && <span style={{ color: C.ph, fontSize: 10, marginLeft: 2 }}>🔒</span>}
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main panel */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ position: 'sticky', top: 52, background: C.white, borderBottom: `1px solid ${C.e8}`, padding: '12px 28px', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: F.display, fontSize: 20, color: C.text }}>{deptLabels[active]}</div>
          <StatusBadge status={product.sectionStatus[active] || 'empty'} />
        </div>
        <div style={{ padding: '24px 28px', maxWidth: 900 }}>
          <StatusPanel p={product} roles={roles} onUpdate={onUpdate} />
          {canEdit(active) ? (
            <CollectionNameContext.Provider value={collectionName}>
              {Form
                ? <Form p={product} onChange={onUpdate} priceTable={priceTable} />
                : <GenericDeptForm p={product} onChange={onUpdate} dept={active} />}
            </CollectionNameContext.Provider>
          ) : active === 'studio' && product.images?.length ? (
            <StudioImagesReadOnly p={product} />
          ) : (
            <div style={{ padding: 32, background: C.fa, borderRadius: 10, border: `1px dashed ${C.e8}`, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: C.muted }}>You don't have access to edit this section.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
